import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Plus, Minus, ShoppingCart, Tag, AlertCircle, Wine, Leaf, Image, Package, ZoomIn, Loader, Download, FileText, X, Sparkles } from 'lucide-react';
import { liquorAPI, ingredientsAPI, bottlesAPI, cartAPI, formatAPI, collectionsAPI } from '../services/api';
import { useAPI, useAsyncOperation, useCartSession } from '../hooks/useAPI';
import { useImagePreloader } from '../hooks/useImagePreloader';
import { usePageTitle } from '../hooks/usePageTitle';
import { mockNewsArticles } from '../data/mock';
import Header from './Header';
import Footer from './Footer';
import LoadingSpinner from './LoadingSpinner';
import ErrorMessage from './ErrorMessage';
import RobustImage from './ui/RobustImage';
import BuilderImage from './ui/BuilderImage';
import CustomerLabelUpload from './CustomerLabelUpload';
import LabelGeneratorAI from './LabelGeneratorAI';
import CanvaTemplateGallery from './CanvaTemplateGallery';
import { useToast } from '../hooks/use-toast';
import SEO from './SEO';
import { organizationSchema, websiteSchema } from '../utils/structuredData';
import { getImageUrlWithFallback } from '../utils/imageUtils';
import { calculateBestPrice, formatPrice, getDiscountTiersDisplay, getNextDiscountTier } from '../utils/discountUtils';
import FacebookPixelService from '../services/facebookPixel';
import { useAuth } from '../contexts/AuthContext';
import '../styles/modern-design.css';

const Builder = () => {
  usePageTitle('Crea il Tuo Amaro');
  
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { user, loading: authLoading } = useAuth();
  const sessionId = useCartSession();
  const { execute: executeAddToCart, loading: addingToCart } = useAsyncOperation();
  const [moqWarning, setMoqWarning] = useState(null); // Ordine minimo validation warning

  // Check if we're coming from a collection with pre-selected product
  const collectionState = location.state;
  
  const [currentStep, setCurrentStep] = useState(collectionState?.skipToLabel ? 5 : 1); // Updated: 5 steps totali (Servizi rimosso)
  const [selectedLiquor, setSelectedLiquor] = useState(null);
  const [selectedIngredients, setSelectedIngredients] = useState([]);
  const [aromaticIntensity, setAromaticIntensity] = useState(3);
  const [selectedFormat, setSelectedFormat] = useState(null); // NEW: Format selection
  const [labelText, setLabelText] = useState('');
  const [labelImage, setLabelImage] = useState(null); // Path to uploaded image
  const [customGraphics, setCustomGraphics] = useState(false); // Custom graphics option (FREE)
  const [uploadLater, setUploadLater] = useState(false); // Upload label later option
  const [selectedBottle, setSelectedBottle] = useState(null);
  const [quantity, setQuantity] = useState(1);
  
  // Digital Services states
  const [digitalServices, setDigitalServices] = useState([]);
  const [selectedDigitalServices, setSelectedDigitalServices] = useState([]);
  
  // 3D Preview states
  const [bottlePreview, setBottlePreview] = useState(null); // URL of generated 3D preview with timestamp
  const [generatingPreview, setGeneratingPreview] = useState(false);
  const [previewZoomed, setPreviewZoomed] = useState(false);
  const [bottleZoomed, setBottleZoomed] = useState(null); // Store the bottle to zoom
  
  // Debug: Monitor bottlePreview changes
  useEffect(() => {
    console.log('🔔 bottlePreview state changed to:', bottlePreview);
  }, [bottlePreview]);
  
  // Label Design Tab state
  const [activeTab, setActiveTab] = useState('upload'); // 'upload', 'ai', 'canva'
  
  // Quote request states
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [quoteNotes, setQuoteNotes] = useState('');
  const [requestingQuote, setRequestingQuote] = useState(false);
  const [showQuoteBanner, setShowQuoteBanner] = useState(true); // Sticky banner visibility
  
  // Collection-specific states
  const [isCollectionProduct, setIsCollectionProduct] = useState(false);
  const [collectionProduct, setCollectionProduct] = useState(null);
  const [collectionInfo, setCollectionInfo] = useState(null);

  // Category configuration states
  const [categoryConfig, setCategoryConfig] = useState({});
  const [availableCategories, setAvailableCategories] = useState([]);

  // Fetch data from APIs
  const { data: liquorTypes, loading: liquorLoading, error: liquorError } = useAPI(() => liquorAPI.getLiquorTypes());
  const { data: allIngredients, loading: ingredientsLoading, error: ingredientsError } = useAPI(() => ingredientsAPI.getIngredients());
  const { data: bottleTypes, loading: bottlesLoading, error: bottlesError } = useAPI(() => bottlesAPI.getBottles());
  const { data: formats, loading: formatsLoading, error: formatsError } = useAPI(() => formatAPI.getAllFormats()); // NEW: Load formats

  // Preload liquor images for faster first step
  const liquorImageUrls = liquorTypes?.map(liquor => getImageUrlWithFallback(liquor.image)) || [];
  const { loaded: imagesPreloaded, progress: imageLoadProgress } = useImagePreloader(
    liquorImageUrls,
    currentStep === 1 && liquorImageUrls.length > 0
  );

  // Fetch Digital Services for optional add-ons
  useEffect(() => {
    const fetchDigitalServices = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/digital-services`);
        if (res.ok) {
          const data = await res.json();
          setDigitalServices(data);
        }
      } catch (error) {
        console.error('Error fetching digital services:', error);
      }
    };
    fetchDigitalServices();
  }, []);

  // Initialize collection product if coming from Collections page
  useEffect(() => {
    const initializeCollectionProduct = async () => {
      if (collectionState?.selectedCollection && collectionState?.selectedProduct) {
        try {
          setIsCollectionProduct(true);
          setCollectionProduct(collectionState.selectedProduct);
          setCollectionInfo(collectionState.selectedCollection);

          // Create a mock liquor type object from collection product
          const mockLiquorType = {
            id: collectionState.selectedProduct.baseProductType,
            name: collectionState.selectedProduct.name,
            description: collectionState.selectedProduct.fullDescription || collectionState.selectedProduct.shortDescription,
            basePrice: collectionState.selectedProduct.basePrice,
            image: collectionState.selectedProduct.mainImage,
            quantityDiscounts: [] // Collections don't use quantity discounts typically
          };
          
          setSelectedLiquor(mockLiquorType);

          // Pre-populate ingredients from collection recipe
          if (collectionState.selectedProduct.ingredients && collectionState.selectedProduct.ingredients.length > 0) {
            const collectionIngredients = collectionState.selectedProduct.ingredients.map(ing => ({
              id: ing.ingredientId,
              name: ing.name,
              category: ing.category || 'botanicals',
              quantity: ing.quantity,
              price: 0 // Collection ingredients are typically included in base price
            }));
            setSelectedIngredients(collectionIngredients);
          }

          // Set default bottle if specified
          if (collectionState.selectedProduct.bottleType && bottleTypes) {
            const matchingBottle = bottleTypes.find(bottle => 
              bottle.id === collectionState.selectedProduct.bottleType || 
              bottle.name.toLowerCase().includes(collectionState.selectedProduct.bottleType.toLowerCase())
            );
            if (matchingBottle) {
              setSelectedBottle(matchingBottle);
            }
          }

          // Pre-fill label text with product name
          if (collectionState.selectedProduct.name) {
            setLabelText(collectionState.selectedProduct.name);
          }

          // If design is included in the collection, enable custom graphics
          if (collectionState.selectedProduct.designIncluded) {
            setCustomGraphics(true);
          }

          toast({
            title: "Prodotto da Collezione Caricato!",
            description: `${collectionState.selectedProduct.name} è stato pre-configurato con la ricetta della collezione.`,
          });

        } catch (error) {
          console.error('Error initializing collection product:', error);
          toast({
            title: "Errore",
            description: "Errore nel caricamento del prodotto dalla collezione",
            variant: "destructive"
          });
        }
      }
    };

    // Only initialize when bottleTypes are loaded (to ensure we can set bottle)
    if (collectionState && bottleTypes && !isCollectionProduct) {
      initializeCollectionProduct();
    }
  }, [collectionState, bottleTypes, isCollectionProduct, toast]);

  // Load category configuration - reload when step changes to ensure fresh data
  useEffect(() => {
    const loadCategoryConfig = async () => {
      try {
        // Use public endpoint for category configuration
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ingredients/public/liquor-categories`, {
          method: 'GET'
        });
        
        if (response.ok) {
          const data = await response.json();
          setCategoryConfig(data.configurations || {});
          setAvailableCategories(data.available_categories || []);
        } else {
          // Fallback to default configuration
          const defaultConfig = {
            gin: { enabled_categories: ['ricette-distillatore', 'botanicals', 'citrus', 'spices'] },
            amaro: { enabled_categories: ['ricette-distillatore', 'botanicals', 'citrus', 'spices'] },
            bitter: { enabled_categories: ['ricette-distillatore', 'botanicals', 'citrus', 'spices'] }
          };
          setCategoryConfig(defaultConfig);
        }
      } catch (error) {
        console.warn('Error loading category config, using defaults:', error);
        // Fallback to default configuration
        const defaultConfig = {
          gin: { enabled_categories: ['ricette-distillatore', 'botanicals', 'citrus', 'spices'] },
          amaro: { enabled_categories: ['ricette-distillatore', 'botanicals', 'citrus', 'spices'] },
          bitter: { enabled_categories: ['ricette-distillatore', 'botanicals', 'citrus', 'spices'] }
        };
        setCategoryConfig(defaultConfig);
      }
    };

    loadCategoryConfig();
  }, [currentStep]); // Reload when step changes to get fresh configuration

  // Helper to format price display (B2B - only for logged in users)
  // Shows prices WITHOUT VAT (IVA esclusa)
  const formatPriceDisplay = (price) => {
    if (!user) {
      return "Accedi per vedere i prezzi";
    }
    const priceWithoutVAT = price / 1.22; // Remove 22% VAT
    return `€${priceWithoutVAT.toFixed(2)}`;
  };

  // Helper to show VAT notice
  const PriceWithVATNotice = ({ price }) => {
    if (!user) {
      return <span className="text-sm text-gray-500">Accedi per vedere i prezzi</span>;
    }
    const priceWithoutVAT = price / 1.22;
    return (
      <div className="flex flex-col items-end">
        <span className="text-lg font-bold text-purple-600">€{priceWithoutVAT.toFixed(2)}</span>
        <span className="text-xs text-gray-500">IVA esclusa</span>
      </div>
    );
  };

  const calculatePrice = () => {
    if (!selectedLiquor) return 0;
    let basePrice = selectedLiquor.basePrice;
    // Botaniche are now free - no cost added for ingredients
    if (selectedBottle) basePrice += selectedBottle.price;
    // Custom graphics service is FREE (no cost added)
    
    // Apply quantity discounts if available
    const quantityDiscounts = selectedLiquor.quantityDiscounts || [];
    const pricing = calculateBestPrice(quantity, basePrice, quantityDiscounts);
    
    return pricing.totalPrice;
  };

  const getPricingDetails = () => {
    if (!selectedLiquor) return null;
    let basePrice = selectedLiquor.basePrice;
    // Botaniche are now free - no cost added for ingredients
    if (selectedBottle) basePrice += selectedBottle.price;
    // Custom graphics service is FREE (no cost added)
    
    const quantityDiscounts = selectedLiquor.quantityDiscounts || [];
    return calculateBestPrice(quantity, basePrice, quantityDiscounts);
  };

  const getDiscountTiers = () => {
    if (!selectedLiquor) return [];
    let basePrice = selectedLiquor.basePrice;
    // Botaniche are now free - no cost added for ingredients
    if (selectedBottle) basePrice += selectedBottle.price;
    // Custom graphics service is FREE (no cost added)
    
    const quantityDiscounts = selectedLiquor.quantityDiscounts || [];
    return getDiscountTiersDisplay(basePrice, quantityDiscounts);
  };

  const addIngredient = (ingredient) => {
    // Controlla se è una "Ricetta dal Distillatore"
    const isDistillerRecipe = ingredient.category === 'ricette-distillatore';
    const hasDistillerRecipe = selectedIngredients.some(ing => ing.category === 'ricette-distillatore');
    
    // Se stiamo aggiungendo una ricetta del distillatore, rimuovi tutti gli altri ingredienti
    if (isDistillerRecipe) {
      if (!selectedIngredients.find(ing => ing.id === ingredient.id)) {
        setSelectedIngredients([ingredient]);
        toast({
          title: "Ricetta del Distillatore selezionata!",
          description: "Questa ricetta esclusiva sostituisce tutti gli altri ingredienti.",
        });
        
        // Auto-advance when distiller recipe selected
        setTimeout(() => {
          if (currentStep === 2) {
            setCurrentStep(3);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }
        }, 800); // Longer delay to see toast message
      }
      return;
    }
    
    // Se c'è già una ricetta del distillatore, non permettere altri ingredienti
    if (hasDistillerRecipe) {
      toast({
        title: "Ricetta Esclusiva Attiva",
        description: "Per aggiungere altri ingredienti, rimuovi prima la Ricetta dal Distillatore.",
        variant: "destructive"
      });
      return;
    }
    
    // Logica normale per ingredienti standard (max 5)
    if (selectedIngredients.length < 5 && !selectedIngredients.find(ing => ing.id === ingredient.id)) {
      const newIngredients = [...selectedIngredients, ingredient];
      setSelectedIngredients(newIngredients);
      
      // Auto-advance when 5 botanicals selected
      if (newIngredients.length === 5) {
        setTimeout(() => {
          if (currentStep === 2) {
            setCurrentStep(3);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }
        }, 500); // Slightly longer delay to see the 5th selection
      }
    }
  };

  const removeIngredient = (ingredientId) => {
    setSelectedIngredients(selectedIngredients.filter(ing => ing.id !== ingredientId));
  };

  // Genera anteprima 3D bottiglia con Nano Banana e salva su server
  const generateBottlePreview = async (labelFile) => {
    if (!labelFile) return;
    
    console.log('🍾 Starting bottle preview generation with label:', labelFile.name, labelFile.size, 'bytes');
    
    setGeneratingPreview(true);
    setBottlePreview(null);
    
    try {
      const formData = new FormData();
      
      // Converti l'URL dell'immagine in File se necessario
      let fileToUpload = labelFile;
      if (typeof labelFile === 'string') {
        // Se è un URL, scarica e converti in blob
        const response = await fetch(labelFile);
        const blob = await response.blob();
        fileToUpload = new File([blob], 'label.png', { type: blob.type });
      }
      
      formData.append('label_file', fileToUpload);
      
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      console.log('📤 Sending to backend for 3D preview generation...');
      
      // Prima genera l'anteprima
      const response = await fetch(`${backendUrl}/api/bottles/generate-preview`, {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) {
        throw new Error('Errore nella generazione dell\'anteprima');
      }
      
      console.log('✅ Backend generated 3D preview');
      
      // Converti la risposta in blob
      const blob = await response.blob();
      
      console.log('📦 Preview blob size:', blob.size, 'bytes');
      
      // Salva il blob generato su server
      const saveFormData = new FormData();
      const previewFile = new File([blob], `preview-${Date.now()}.png`, { type: 'image/png' });
      saveFormData.append('file', previewFile);
      
      console.log('💾 Saving preview to server...');
      
      const saveResponse = await fetch(`${backendUrl}/api/upload/builder/label`, {
        method: 'POST',
        body: saveFormData
      });
      
      const saveResult = await saveResponse.json();
      
      if (saveResponse.ok && saveResult.success) {
        // Save relative path with timestamp object to force re-render
        const previewData = {
          path: saveResult.filePath,
          timestamp: Date.now()
        };
        console.log('🎨 New bottle preview generated:', saveResult.filePath);
        console.log('📍 Setting bottlePreview state to:', previewData);
        setBottlePreview(previewData);
        
        toast({
          title: "Anteprima Generata!",
          description: "La tua bottiglia 3D è pronta. Clicca per ingrandire.",
        });
      } else {
        throw new Error('Errore nel salvataggio dell\'anteprima');
      }
      
    } catch (error) {
      console.error('Error generating preview:', error);
      toast({
        title: "Errore",
        description: "Impossibile generare l'anteprima. Riprova.",
        variant: "destructive"
      });
    } finally {
      setGeneratingPreview(false);
    }
  };
  
  // Helper to get full URL from path
  const getImageUrl = (pathOrObject) => {
    if (!pathOrObject) return null;
    
    // Handle both string and object format
    const path = typeof pathOrObject === 'string' ? pathOrObject : pathOrObject.path;
    
    if (path.startsWith('http')) return path;
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    // Add cache-busting timestamp to force reload
    const cacheBuster = `?t=${Date.now()}`;
    return `${backendUrl}/api/upload/serve/${path}${cacheBuster}`;
  };

  // Download anteprima 3D
  const downloadPreview = () => {
    if (!bottlePreview) return;
    
    const link = document.createElement('a');
    link.href = getImageUrl(bottlePreview);
    link.download = `anteprima-bottiglia-${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast({
      title: "Download avviato",
      description: "L'anteprima 3D è in download",
    });
  };
  
  // Handler per etichetta generata dall'AI
  const handleAILabelGenerated = (filePath) => {
    console.log('🎨 AI Label generated with path:', filePath);
    setLabelImage(filePath);
    
    // Genera anteprima 3D
    // Converti path in file per generare preview
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const imageUrl = `${backendUrl}/api/upload/serve/${filePath}`;
    
    console.log('🔄 Fetching label from:', imageUrl);
    
    fetch(imageUrl)
      .then(res => res.blob())
      .then(blob => {
        console.log('✅ Label blob received, size:', blob.size, 'bytes');
        const file = new File([blob], 'ai-generated-label.png', { type: 'image/png' });
        generateBottlePreview(file);
      })
      .catch(err => {
        console.error('Error loading AI label for preview:', err);
      });
  };

  // Funzione helper per controllare se un ingrediente può essere selezionato
  const canSelectIngredient = (ingredient) => {
    const isDistillerRecipe = ingredient.category === 'ricette-distillatore';
    const hasDistillerRecipe = selectedIngredients.some(ing => ing.category === 'ricette-distillatore');
    const isAlreadySelected = selectedIngredients.find(ing => ing.id === ingredient.id);
    
    // Se l'ingrediente è già selezionato, può sempre essere deselezionato
    if (isAlreadySelected) return true;
    
    // Se è una ricetta del distillatore, può sempre essere selezionata (sostituirà gli altri)
    if (isDistillerRecipe) return true;
    
    // Se c'è già una ricetta del distillatore attiva, non si possono aggiungere altri ingredienti
    if (hasDistillerRecipe) return false;
    
    // Altrimenti segui la logica normale (max 5 ingredienti)
    return selectedIngredients.length < 5;
  };

  // Animazione smooth scroll migliorata
  const smoothScrollToTop = (callback) => {
    // Aggiungi classe per animazione loading
    document.body.classList.add('step-changing');
    
    // Scroll animato al top con easing personalizzato
    const startPosition = window.pageYOffset;
    const duration = 800; // Durata animazione in ms
    let startTime = null;
    
    function easeInOutCubic(t) {
      return t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;
    }
    
    function animation(currentTime) {
      if (startTime === null) startTime = currentTime;
      const timeElapsed = currentTime - startTime;
      const progress = Math.min(timeElapsed / duration, 1);
      
      const ease = easeInOutCubic(progress);
      window.scrollTo(0, startPosition * (1 - ease));
      
      if (progress < 1) {
        requestAnimationFrame(animation);
      } else {
        // Rimuovi classe animazione e esegui callback
        setTimeout(() => {
          document.body.classList.remove('step-changing');
          if (callback) callback();
        }, 100);
      }
    }
    
    requestAnimationFrame(animation);
  };

  const nextStep = () => {
    if (currentStep < 6) {
      smoothScrollToTop(() => {
        // Skip step 4 (Servizi rimosso): 3 → 5
        const next = currentStep + 1 === 4 ? 5 : currentStep + 1;
        setCurrentStep(next);
      });
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      smoothScrollToTop(() => {
        // Skip step 4 (Servizi rimosso): 5 → 3
        const prev = currentStep - 1 === 4 ? 3 : currentStep - 1;
        setCurrentStep(prev);
      });
    }
  };

  // Navigazione diretta a uno step specifico
  const goToStep = (step) => {
    // Servizi rimosso: non permettere navigazione allo step 4
    if (step === 4) return;
    // Permetti di andare solo agli step precedenti o al corrente
    // Per andare avanti, deve aver completato i requisiti
    if (step <= currentStep || canGoToStep(step)) {
      smoothScrollToTop(() => {
        setCurrentStep(step);
      });
    }
  };

  // Controlla se si può andare a un determinato step
  const canGoToStep = (step) => {
    // For collection products, skip steps 1 and 2 (liquor and ingredients are pre-set)
    if (isCollectionProduct) {
      switch (step) {
        case 1:
        case 2:
          return false; // Skip these steps for collection products
        case 3:
          return selectedLiquor !== null; // Format step
        case 4:
          return selectedLiquor !== null; // Digital Services step
        case 5:
          return selectedLiquor !== null; // Label step
        case 6:
          return selectedLiquor !== null; // Bottle step
        default:
          return false;
      }
    }
    
    // Normal logic for custom products
    switch (step) {
      case 1:
        return true; // Sempre possibile tornare al primo step
      case 2:
        return selectedLiquor !== null; // Serve aver scelto un liquore
      case 3:
        return selectedLiquor !== null; // Format step
      case 4:
        return selectedLiquor !== null; // Digital Services step
      case 5:
        return selectedLiquor !== null; // Label step
      case 6:
        return selectedLiquor !== null; // Bottle step
      default:
        return false;
    }
  };

  // Icone per ogni step
  const getStepIcon = (step) => {
    const icons = {
      1: Wine,
      2: Leaf,
      3: Wine, // Format (bottle size)
      4: Sparkles, // Digital Services
      5: Image,   // Label
      6: Package  // Bottle
    };
    return icons[step] || Wine;
  };

  const addToCart = async () => {
    try {
      // Calculate base price components (botaniche are free)
      let basePrice = selectedLiquor.basePrice;
      
      // Apply format multiplier to base price
      if (selectedFormat) {
        basePrice = basePrice * selectedFormat.price_multiplier;
      }
      
      // Botaniche are now free - no cost added for ingredients
      if (selectedBottle) basePrice += selectedBottle.price;
      // Custom graphics service is FREE (no cost added)

      // Check if user is on /campioni page
      const isSamplesPage = location.pathname === '/campioni' || location.pathname.includes('/campioni');
      
      // Get selected digital services details
      const selectedServicesData = digitalServices
        .filter(s => selectedDigitalServices.includes(s.id))
        .map(s => ({ id: s.id, name: s.name, category: s.category }));

      const cartItem = {
        id: `${isCollectionProduct ? 'collection' : 'custom'}_${Date.now()}`,
        type: isCollectionProduct ? 'collection' : 'custom',
        isSample: isSamplesPage, // Mark as sample if added from /campioni page
        productData: {
          name: selectedLiquor?.name || 'Liquore Personalizzato', // Add the product name
          liquorType: selectedLiquor?.name, // Also save as liquorType for compatibility
          liquor: selectedLiquor,
          ingredients: selectedIngredients,
          intensity: aromaticIntensity,
          format: selectedFormat, // NEW: Include selected format
          label: { 
            text: labelText, 
            image: labelImage, 
            customGraphics: customGraphics,
            uploadLater: uploadLater,
            bottlePreview: bottlePreview // 3D preview URL
          },
          bottle: selectedBottle || (bottleTypes && bottleTypes[0]),
          basePrice: basePrice, // Add the calculated base price
          quantityDiscounts: selectedLiquor.quantityDiscounts || [],
          // Digital services selected by user (optional)
          digitalServices: selectedServicesData,
          // Collection-specific data
          ...(isCollectionProduct && {
            collectionId: collectionInfo?.id,
            collectionName: collectionInfo?.name,
            collectionTitle: collectionInfo?.title,
            productId: collectionProduct?.id,
            isPreConfigured: true
          })
        },
        quantity,
        price: calculatePrice()
      };

      await executeAddToCart(async () => {
        const result = await cartAPI.addToCart(sessionId, cartItem);
        if (result.success) {
          // Track Facebook Pixel - AddToCart event
          FacebookPixelService.trackAddToCart({
            content_name: selectedLiquor?.name || 'Liquore Personalizzato',
            content_category: isCollectionProduct ? 'collection' : 'custom',
            content_type: 'product',
            value: calculatePrice(),
            currency: 'EUR',
            content_ids: [cartItem.id],
            quantity: quantity
          });

          toast({
            title: "Prodotto aggiunto al carrello!",
            description: `${selectedLiquor?.name} personalizzato aggiunto con successo.`,
          });
          navigate('/carrello');
        } else {
          throw new Error(result.message || 'Errore nell\'aggiungere al carrello');
        }
      });
    } catch (error) {
      toast({
        title: "Errore",
        description: "Impossibile aggiungere al carrello. Riprova.",
        variant: "destructive"
      });
    }
  };

  const handleRequestQuote = async () => {
    if (!user) {
      toast({
        title: "Accesso richiesto",
        description: "Effettua il login per richiedere un preventivo",
        variant: "destructive"
      });
      navigate('/login');
      return;
    }

    setShowQuoteModal(true);
  };

  const submitQuoteRequest = async () => {
    try {
      setRequestingQuote(true);

      // Prepare product configuration
      const productConfig = {
        liquor: selectedLiquor?.name,
        ingredients: selectedIngredients.map(ing => ing.name).join(', '),
        intensity: aromaticIntensity,
        format: selectedFormat?.name,
        bottle: selectedBottle?.name,
        quantity: quantity,
        notes: quoteNotes,
        estimatedPrice: calculatePrice()
      };

      // Call quote API
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/quotes/customer/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          product_configuration: productConfig,
          customer_notes: quoteNotes,
          requested_quantity: quantity
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast({
          title: "Preventivo richiesto!",
          description: `Richiesta ${data.quote_number} inviata. Ti risponderemo entro 24-48 ore.`,
        });
        
        setShowQuoteModal(false);
        setQuoteNotes('');
        navigate('/dashboard?tab=quotes');
      } else {
        throw new Error(data.detail || 'Errore nella richiesta');
      }
    } catch (error) {
      toast({
        title: "Errore",
        description: error.message || "Impossibile inviare la richiesta preventivo",
        variant: "destructive"
      });
    } finally {
      setRequestingQuote(false);
    }
  };


  const canProceed = () => {
    // For collection products, different logic
    if (isCollectionProduct) {
      switch (currentStep) {
        case 1:
        case 2:
          return true; // Always can proceed from these steps for collections
        case 3:
          return selectedFormat; // Format step
        case 4:
          return true; // Digital services step - always can proceed (optional)
        case 5: 
          return customGraphics || uploadLater || labelText.trim().length > 0;
        case 6: 
          return selectedBottle;
        default: 
          return false;
      }
    }
    
    // Normal logic for custom products
    switch (currentStep) {
      case 1: return selectedLiquor;
      case 2: return selectedIngredients.length > 0;
      case 3: return selectedFormat; // Format step
      case 4: return true; // Digital services step - always can proceed (optional)
      case 5: return customGraphics || uploadLater || labelText.trim().length > 0;
      case 6: return selectedBottle;
      default: return false;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Banner Sticky Richiesta Preventivo B2B - Removed */}
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-32 md:pb-8">
        {/* Collection Product Banner */}
        {isCollectionProduct && collectionProduct && (
          <div className="mb-8 bg-gradient-to-r from-purple-600 to-purple-800 text-white rounded-lg p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center mb-2">
                  <Package className="w-5 h-5 mr-2" />
                  <span className="text-sm font-medium opacity-90">
                    Collezione: {collectionInfo?.title}
                  </span>
                </div>
                <h2 className="text-2xl font-bold mb-2">{collectionProduct.name}</h2>
                <p className="text-purple-100 text-sm leading-relaxed">
                  {collectionProduct.shortDescription}
                </p>
                <div className="flex items-center mt-3 space-x-4 text-sm">
                  <span className="bg-white/20 px-2 py-1 rounded">
                    {collectionProduct.alcoholPercentage}° • {collectionProduct.volume}ML
                  </span>
                  <span className="bg-white/20 px-2 py-1 rounded">
                    Ricetta Preimpostata
                  </span>
                </div>
              </div>
              {/* Price Removed */}
            </div>
          </div>
        )}

        {/* Progress Bar */}
        <div className="mb-12">
          {/* Mobile: Layout compatto con label integrate */}
          <div className="md:hidden">
            <div className="overflow-x-auto pb-2 -mx-4 px-4">
              <div className="flex items-start gap-8 min-w-max px-4">
                {[1, 2, 3, 5, 6].map((step) => {
                  const StepIcon = getStepIcon(step);
                  const isActive = currentStep >= step;
                  const isClickable = step <= currentStep || canGoToStep(step);
                  const isCollectionSkipped = isCollectionProduct && (step === 1 || step === 2);
                  const stepLabels = { 1: 'Liquore', 2: 'Ricetta', 3: 'Formato', 5: 'Etichetta', 6: 'Bottiglia' };
                  
                  return (
                    <div key={step} className="flex flex-col items-center flex-shrink-0 relative">
                      {/* Step Circle */}
                      <button
                        onClick={() => isClickable && goToStep(step)}
                        disabled={!isClickable}
                        className={`w-12 h-12 rounded-full flex items-center justify-center font-semibold transition-all duration-200 mb-2 ${
                          isCollectionSkipped
                            ? 'bg-green-500 text-white shadow-lg' 
                            : isActive 
                            ? 'bg-purple-600 text-white shadow-lg' 
                            : 'bg-gray-200 text-gray-600'
                        } ${
                          isClickable 
                            ? 'active:scale-95 cursor-pointer' 
                            : 'cursor-not-allowed opacity-50'
                        }`}
                      >
                        <StepIcon className="w-5 h-5" />
                      </button>
                      
                      {/* Step Label */}
                      <span className={`text-xs font-medium text-center whitespace-nowrap ${
                        isCollectionSkipped 
                          ? 'text-green-600' 
                          : isActive 
                          ? 'text-purple-600' 
                          : 'text-gray-500'
                      }`}>
                        {isCollectionSkipped ? '✓ ' : ''}{stepLabels[step]}
                      </span>
                      
                      {/* Connector Line */}
                      {step < 6 && (
                        <div className={`absolute top-6 left-full w-8 h-1 rounded -ml-4 ${
                          isCollectionProduct && step <= 2
                            ? 'bg-green-500'
                            : currentStep > step ? 'bg-purple-600' : 'bg-gray-200'
                        }`} />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            
            {/* Progress indicator dots */}
            <div className="mt-4 flex justify-center gap-1.5">
              {[1, 2, 3, 4, 5, 6].map((step) => (
                <div
                  key={step}
                  className={`h-1 rounded-full transition-all ${
                    currentStep >= step ? 'w-8 bg-purple-600' : 'w-4 bg-gray-300'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Desktop: Layout originale orizzontale */}
          <div className="hidden md:block">
            <div className="flex items-center justify-between mb-4">
              {[1, 2, 3, 5, 6].map((step) => {
              const StepIcon = getStepIcon(step);
              const isActive = currentStep >= step;
              const isClickable = step <= currentStep || canGoToStep(step);
              const isCollectionSkipped = isCollectionProduct && (step === 1 || step === 2);
              
              return (
                <div key={step} className="flex items-center">
                  <button
                    onClick={() => isClickable && goToStep(step)}
                    disabled={!isClickable}
                    className={`w-12 h-12 rounded-full flex items-center justify-center font-semibold transition-all duration-200 ${
                      isCollectionSkipped
                        ? 'bg-green-500 text-white shadow-lg'
                        : isActive 
                        ? 'bg-purple-600 text-white shadow-lg' 
                        : 'bg-gray-200 text-gray-600'
                    } ${
                      isClickable 
                        ? 'hover:scale-105 hover:shadow-md cursor-pointer' 
                        : 'cursor-not-allowed opacity-50'
                    }`}
                    title={`Step ${step}: ${
                      isCollectionSkipped 
                        ? 'Pre-configurato dalla collezione'
                        : step === 1 ? 'Scegli il liquore' :
                          step === 2 ? 'Personalizza la ricetta' :
                          step === 3 ? 'Scegli il formato' :
                          step === 4 ? 'Servizi aggiuntivi' :
                          step === 5 ? 'Crea l\'etichetta' :
                          step === 6 ? 'Scegli la bottiglia' :
                          'Step non definito'
                    }`}
                  >
                    <StepIcon className="w-5 h-5" />
                  </button>
                  {step < 6 && (
                    <div className={`flex-1 h-2 mx-4 rounded ${
                      isCollectionProduct && step <= 2
                        ? 'bg-green-500'
                        : currentStep > step ? 'bg-purple-600' : 'bg-gray-200'
                    }`} />
                  )}
                </div>
              );
            })}
            </div>
            <div className="flex justify-between text-sm font-medium text-gray-600">
              <span className={isCollectionProduct ? 'text-green-600' : ''}>
                {isCollectionProduct ? '✓ Liquore' : 'Liquore'}
              </span>
              <span className={isCollectionProduct ? 'text-green-600' : ''}>
                {isCollectionProduct ? '✓ Ricetta' : 'Ricetta'}
              </span>
              <span>Formato</span>
              <span>Etichetta</span>
              <span>Bottiglia</span>
            </div>
          </div>
        </div>

        {/* Navigation Buttons - Top */}
        <div className="mt-6 max-w-md mx-auto">
          <div className="flex gap-3">
            {currentStep > 1 && (
              <button
                onClick={prevStep}
                className="flex-1 flex items-center justify-center px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200 font-medium"
              >
                <ChevronLeft className="mr-1 w-4 h-4" />
                Indietro
              </button>
            )}
            {currentStep < 6 ? (
              <button
                onClick={nextStep}
                disabled={!canProceed()}
                className={`${currentStep > 1 ? 'flex-1' : 'w-full'} flex items-center justify-center px-4 py-2.5 rounded-lg font-medium transition-colors duration-200 ${
                  canProceed()
                    ? 'bg-purple-600 text-white hover:bg-purple-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Continua
                <ChevronRight className="ml-1 w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={addToCart}
                disabled={!canProceed() || addingToCart}
                className={`${currentStep > 1 ? 'flex-1' : 'w-full'} flex items-center justify-center px-4 py-2.5 rounded-lg font-medium transition-colors duration-200 ${
                  canProceed() && !addingToCart
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {addingToCart ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Aggiungendo...
                  </>
                ) : (
                  <>
                    <ShoppingCart className="mr-1 w-4 h-4" />
                    Aggiungi al Preventivo
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        <div className="step-content">
          {/* Main Content */}
          <div className="w-full">
            <>
              {/* Collection Product Info */}
            {isCollectionProduct && (currentStep === 1 || currentStep === 2) && (
              <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 lg:p-8">
                <div className="text-center max-w-2xl mx-auto">
                  <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Package className="w-8 h-8 text-green-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Prodotto Pre-Configurato</h2>
                  <p className="text-gray-600 mb-6">
                    Questo prodotto fa parte della collezione "{collectionInfo?.title}" e ha una ricetta già bilanciata 
                    dai nostri maestri distillatori. Puoi procedere direttamente alla personalizzazione dell'etichetta.
                  </p>
                  <div className="bg-gray-50 rounded-lg p-4 mb-6">
                    <h3 className="font-semibold text-gray-900 mb-3">Ricetta Inclusa:</h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <strong>Liquore:</strong> {collectionProduct?.name}
                      </div>
                      <div>
                        <strong>Gradazione:</strong> {collectionProduct?.alcoholPercentage}°
                      </div>
                      {collectionProduct?.ingredients && collectionProduct.ingredients.length > 0 && (
                        <div className="col-span-2">
                          <strong>Ingredienti:</strong> {collectionProduct.ingredients.map(ing => ing.name).join(', ')}
                        </div>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => setCurrentStep(3)}
                    className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 font-semibold"
                  >
                    Procedi alla Personalizzazione dell'Etichetta
                  </button>
                </div>
              </div>
            )}

            {/* Step 1: Choose Liquor */}
              {currentStep === 1 && !isCollectionProduct && (
              <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 lg:p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Scegli il Tuo Liquore</h2>
                {liquorLoading ? (
                  <LoadingSpinner message="Caricamento liquori..." />
                ) : liquorError ? (
                  <ErrorMessage error="Errore nel caricamento dei liquori" />
                ) : (
                  <div className="grid md:grid-cols-3 gap-6">
                    {(liquorTypes || []).map((liquor) => (
                    <div
                      key={liquor.id}
                      className={`border-2 rounded-lg p-6 cursor-pointer transition-all duration-300 hover:shadow-lg ${
                        selectedLiquor?.id === liquor.id ? 'border-purple-600 bg-purple-50' : 'border-gray-200 hover:border-purple-300'
                      }`}
                      onClick={() => {
                        setSelectedLiquor(liquor);
                        // Track Facebook Pixel - ViewContent event
                        FacebookPixelService.trackViewContent({
                          content_name: liquor.name,
                          content_category: 'liquor',
                          content_type: 'product',
                          value: liquor.basePrice,
                          currency: 'EUR',
                          content_ids: [liquor.id]
                        });
                        // Auto-advance to next step with scroll to top
                        setTimeout(() => {
                          if (currentStep === 1) {
                            setCurrentStep(2);
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }
                        }, 300); // Small delay for visual feedback
                      }}
                    >
                      <div className="aspect-square w-full overflow-hidden rounded-lg mb-4">
                        <BuilderImage
                          src={getImageUrlWithFallback(liquor.image)} 
                          alt={liquor.name} 
                          className="rounded-lg"
                          priority={true}
                        />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">{liquor.name}</h3>
                      <p className="text-gray-600 text-sm mb-3">{liquor.description}</p>
                    </div>
                  ))}
                </div>
                )}

                {/* Quantity Discounts Section Removed */}
              </div>
            )}

            {/* Step 2: Customize Recipe */}
            {currentStep === 2 && !isCollectionProduct && (
              <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 lg:p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Personalizza la Ricetta</h2>
                
                {/* Selected Ingredients */}
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Botaniche Selezionate ({selectedIngredients.length}/5)</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Scegli una delle ricette collaudate dal nostro Mastro Distillatore oppure crea la tua miscela unica selezionando fino a 5 ingredienti personalizzati.
                  </p>
                  <div className="flex flex-wrap gap-1 sm:gap-2 md:gap-3 min-h-[60px] p-2 sm:p-3 lg:p-4 border-2 border-dashed border-gray-300 rounded-lg overflow-hidden">
                    {selectedIngredients.map((ingredient) => (
                      <div key={ingredient.id} className="flex items-center bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-xs max-w-full">
                        <span className="truncate flex-1">{ingredient.name}</span>
                        <button
                          onClick={() => removeIngredient(ingredient.id)}
                          className="ml-1 hover:text-purple-900 flex-shrink-0"
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                    {selectedIngredients.length === 0 && (
                      <p className="text-gray-500 italic">Seleziona gli ingredienti dalla lista sottostante</p>
                    )}
                  </div>
                </div>

                {/* Available Ingredients */}
                {ingredientsLoading ? (
                  <LoadingSpinner message="Caricamento ingredienti..." />
                ) : ingredientsError ? (
                  <ErrorMessage error="Errore nel caricamento degli ingredienti" />
                ) : (
                  <div className="space-y-6">
                    {/* Group ingredients by category - filtered by liquor type configuration */}
                    {(() => {
                      // Get enabled categories for the selected liquor type
                      const liquorType = selectedLiquor?.id || selectedLiquor?.type || 'gin';
                      const enabledCategories = categoryConfig[liquorType]?.enabled_categories || ['ricette-distillatore', 'botanicals', 'citrus', 'spices'];
                      
                      return enabledCategories.map((category) => {
                        const categoryIngredients = (allIngredients || []).filter(ing => {
                          if (ing.category !== category || ing.available === false) return false;
                          // Restrizione per prodotto: se allowed_product_ids è popolato, il liquor selezionato deve essere incluso
                          const allowed = ing.allowed_product_ids;
                          if (Array.isArray(allowed) && allowed.length > 0) {
                            return selectedLiquor?.id ? allowed.includes(selectedLiquor.id) : false;
                          }
                          return true;
                        });
                        if (categoryIngredients.length === 0) return null;
                      
                      return (
                        <div key={category}>
                          <h4 className="text-md font-semibold text-gray-900 mb-3 capitalize">
                            {category === 'botanicals' ? 'Botaniche' : 
                             category === 'citrus' ? 'Agrumi' : 
                             category === 'spices' ? 'Spezie' :
                             category === 'ricette-distillatore' ? 'Ricette dal Distillatore' : category}
                          </h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3">
                            {categoryIngredients.map((ingredient) => (
                          <button
                            key={ingredient.id}
                            onClick={() => addIngredient(ingredient)}
                            disabled={!canSelectIngredient(ingredient)}
                            className={`p-2 sm:p-3 lg:p-4 rounded-lg border text-sm font-medium transition-colors duration-200 min-h-[100px] sm:min-h-[120px] lg:min-h-[160px] xl:min-h-[180px] ${
                              selectedIngredients.find(ing => ing.id === ingredient.id)
                                ? ingredient.category === 'ricette-distillatore'
                                  ? 'bg-amber-100 border-amber-300 text-amber-800'
                                  : 'bg-purple-100 border-purple-300 text-purple-800'
                                : !canSelectIngredient(ingredient)
                                ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                                : ingredient.category === 'ricette-distillatore'
                                ? 'bg-white border-amber-200 text-amber-700 hover:border-amber-300 hover:bg-amber-50'
                                : 'bg-white border-gray-200 text-gray-700 hover:border-purple-300 hover:bg-purple-50'
                            }`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex flex-col">
                                <span className="font-medium">{ingredient.name}</span>
                                {ingredient.category === 'ricette-distillatore' && (
                                  <span className="text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded mt-1 font-medium">
                                    🧪 Ricetta Esclusiva
                                  </span>
                                )}
                              </div>
                              <Plus className="w-4 h-4" />
                            </div>
                            {ingredient.image && (
                              <div className="aspect-square w-full overflow-hidden rounded mb-2">
                                <img 
                                  src={getImageUrlWithFallback(ingredient.image)} 
                                  alt={ingredient.name}
                                  className="w-full h-full object-cover"
                                  loading="lazy"
                                  decoding="async"
                                />
                              </div>
                            )}
                            </button>
                          ))}
                          </div>
                        </div>
                        );
                      });
                    })()}
                  </div>
                )}

                {/* Aromatic Intensity */}
                <div className="mt-8">
                  <h4 className="text-md font-semibold text-gray-900 mb-4">Intensità Aromatica</h4>
                  <div className="flex items-center space-x-4">
                    <span className="text-sm text-gray-600">Leggera</span>
                    <input
                      type="range"
                      min="1"
                      max="5"
                      value={aromaticIntensity}
                      onChange={(e) => setAromaticIntensity(parseInt(e.target.value))}
                      className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                    />
                    <span className="text-sm text-gray-600">Intensa</span>
                    <span className="text-sm font-semibold text-purple-600">Livello {aromaticIntensity}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Choose Format - NEW */}
            {currentStep === 3 && (
              <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 lg:p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Scegli il Formato</h2>
                
                {formatsLoading ? (
                  <div className="flex justify-center py-12">
                    <Loader className="w-8 h-8 animate-spin text-purple-600" />
                  </div>
                ) : formatsError ? (
                  <div className="text-center py-12">
                    <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                    <p className="text-red-600">Errore nel caricamento dei formati</p>
                  </div>
                ) : (
                  <>
                    <p className="text-gray-600 mb-6">
                      Seleziona la dimensione della bottiglia. Il prezzo varia in base al formato.
                    </p>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {formats && formats.map((format) => (
                        <div
                          key={format.id}
                          onClick={() => {
                            setSelectedFormat(format);
                            // Auto-advance to next step with scroll to top
                            // Servizi rimosso: da step 3 (Formato) si passa direttamente a step 5 (Etichetta)
                            setTimeout(() => {
                              if (currentStep === 3) {
                                setCurrentStep(5);
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                              }
                            }, 300);
                          }}
                          className={`relative cursor-pointer rounded-lg p-6 border-2 transition-all duration-200 hover:shadow-lg ${
                            selectedFormat?.id === format.id
                              ? 'border-purple-600 bg-purple-50 shadow-md'
                              : 'border-gray-200 hover:border-purple-300'
                          }`}
                        >
                          {/* Selected Indicator */}
                          {selectedFormat?.id === format.id && (
                            <div className="absolute top-2 right-2">
                              <div className="w-6 h-6 bg-purple-600 rounded-full flex items-center justify-center">
                                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              </div>
                            </div>
                          )}
                          
                          {/* Format Icon */}
                          <div className="mb-3">
                            <Wine className={`w-12 h-12 mx-auto ${
                              selectedFormat?.id === format.id ? 'text-purple-600' : 'text-gray-400'
                            }`} />
                          </div>
                          
                          {/* Format Name */}
                          <h3 className="text-xl font-bold text-gray-900 text-center mb-2">
                            {format.name}
                          </h3>
                          
                          {/* Format Size */}
                          <p className="text-sm text-gray-600 text-center mb-3">
                            {format.size_ml}ml
                          </p>
                          
                          {/* Price Indicator Removed */}
                          
                          {/* Ordine Minimo Badge */}
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <p className="text-xs text-gray-600 text-center">
                              Ordine minimo: {format.size_cl === 20 || format.size_cl === 5 ? '120' : format.min_order_quantity} pz
                            </p>
                            {(format.size_cl === 20 || format.size_cl === 5) && (
                              <p className="text-xs text-purple-600 font-semibold text-center mt-1">
                                {format.size_cl}cl: minimo 120 pezzi
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Selected Format Info */}
                    {selectedFormat && (
                      <div className="mt-6 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                        <h4 className="font-semibold text-purple-900 mb-2">Formato Selezionato: {selectedFormat.name}</h4>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-gray-600">Dimensione:</p>
                            <p className="font-medium text-gray-900">{selectedFormat.size_cl}cl ({selectedFormat.size_ml}ml)</p>
                          </div>
                          <div>
                            <p className="text-gray-600">Ordine Minimo:</p>
                            <p className="font-medium text-gray-900">{selectedFormat.min_order_quantity} bottiglie</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Step 4: Digital Services */}
            {currentStep === 4 && (
              <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 lg:p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Servizi Aggiuntivi</h2>
                <p className="text-gray-600 mb-6">
                  Seleziona i servizi opzionali di cui hai bisogno. Riceverai un preventivo personalizzato.
                </p>

                {digitalServices.length > 0 ? (
                  <>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {digitalServices.map(service => {
                        const isSelected = selectedDigitalServices.includes(service.id);
                        return (
                          <label
                            key={service.id}
                            className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all hover:shadow-md ${
                              isSelected 
                                ? 'border-purple-500 bg-purple-50 shadow-md' 
                                : 'border-gray-200 hover:border-purple-300 bg-white'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedDigitalServices(prev => [...prev, service.id]);
                                } else {
                                  setSelectedDigitalServices(prev => prev.filter(id => id !== service.id));
                                }
                              }}
                              className="mt-1 h-5 w-5 text-purple-600 rounded focus:ring-purple-500"
                            />
                            <div className="flex-1">
                              <span className="font-semibold text-gray-900 block">{service.name}</span>
                              {service.short_description && (
                                <span className="text-sm text-gray-500 block mt-1">{service.short_description}</span>
                              )}
                              {service.category && (
                                <span className="inline-block mt-2 text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                                  {service.category}
                                </span>
                              )}
                            </div>
                          </label>
                        );
                      })}
                    </div>

                    {selectedDigitalServices.length > 0 && (
                      <div className="mt-6 p-4 bg-purple-100 rounded-xl">
                        <p className="text-purple-800 font-medium">
                          ✨ <strong>{selectedDigitalServices.length}</strong> {selectedDigitalServices.length === 1 ? 'servizio selezionato' : 'servizi selezionati'}
                        </p>
                        <p className="text-sm text-purple-600 mt-1">
                          Saranno inclusi nel tuo preventivo
                        </p>
                      </div>
                    )}

                    <div className="mt-6 p-4 bg-gray-50 rounded-xl border border-gray-200">
                      <p className="text-sm text-gray-600">
                        💡 <strong>Nota:</strong> Puoi anche saltare questo passaggio e procedere senza servizi aggiuntivi.
                      </p>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-12 bg-gray-50 rounded-xl">
                    <p className="text-gray-500 mb-4">Nessun servizio disponibile al momento.</p>
                    <p className="text-sm text-gray-400">Puoi procedere al passaggio successivo.</p>
                  </div>
                )}
              </div>
            )}

            {/* Step 5: Create Label */}
            {currentStep === 5 && (
              <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 lg:p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Design dell'Etichetta</h2>
                
                <div className="grid md:grid-cols-2 gap-8">
                  {/* Left Column - Design Options with Tabs */}
                  <div>
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Nome del Liquore
                        </label>
                        <input
                          type="text"
                          value={labelText}
                          onChange={(e) => setLabelText(e.target.value)}
                          placeholder="Inserisci il nome del tuo liquore"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          maxLength={50}
                        />
                        <p className="text-xs text-gray-500 mt-1">{labelText.length}/50 caratteri</p>
                      </div>

                      <h4 className="text-md font-semibold text-gray-900 mb-4">Design Etichetta</h4>
                      
                      {/* Upload Later Option - Always visible */}
                      <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 mb-4">
                        <div className="flex items-start">
                          <input
                            type="checkbox"
                            id="uploadLater"
                            checked={uploadLater}
                            onChange={(e) => {
                              setUploadLater(e.target.checked);
                              if (e.target.checked) {
                                setLabelImage(null);
                                setCustomGraphics(false);
                                setBottlePreview(null);
                                // Auto-advance to next step
                                setTimeout(() => {
                                  if (currentStep === 5) {
                                    setCurrentStep(6);
                                    window.scrollTo({ top: 0, behavior: 'smooth' });
                                  }
                                }, 300);
                              }
                            }}
                            className="mt-1 h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                          />
                          <label htmlFor="uploadLater" className="ml-3 flex-1">
                            <span className="font-medium text-gray-900">Carica più tardi</span>
                            <p className="text-sm text-gray-600 mt-1">
                              Carica la tua etichetta dopo aver completato l'ordine
                            </p>
                          </label>
                        </div>
                      </div>

                      {/* Custom Graphics Option - Always FREE */}
                      {!uploadLater && (
                        <div className="bg-green-50 border-2 border-green-400 rounded-lg p-4 mb-4">
                          <div className="flex items-start">
                            <input
                              type="checkbox"
                              id="customGraphics"
                              checked={customGraphics}
                              onChange={(e) => {
                                setCustomGraphics(e.target.checked);
                                if (e.target.checked) {
                                  setLabelImage(null);
                                  setBottlePreview(null);
                                  // Auto-advance to next step
                                  setTimeout(() => {
                                    if (currentStep === 4) {
                                      setCurrentStep(5);
                                      window.scrollTo({ top: 0, behavior: 'smooth' });
                                    }
                                  }, 300);
                                }
                              }}
                              className="mt-1 h-4 w-4 text-green-600 rounded focus:ring-green-500"
                            />
                            <label htmlFor="customGraphics" className="ml-3 flex-1 cursor-pointer">
                              <div className="flex items-center justify-between">
                                <span className="font-medium text-gray-900">Design Professionale</span>
                                <span className="text-green-600 font-bold text-sm bg-green-100 px-3 py-1 rounded-full">
                                  GRATIS
                                </span>
                              </div>
                              <p className="text-sm text-gray-600 mt-1">
                                Ti creeremo una grafica personalizzata professionale
                              </p>
                            </label>
                          </div>
                        </div>
                      )}

                      {/* Tabs for Label Design */}
                      {!uploadLater && !customGraphics && (
                        <div>
                          {/* Tab Headers */}
                          <div className="flex border-b border-gray-200 mb-4">
                            <button
                              onClick={() => setActiveTab('upload')}
                              className={`flex-1 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                                activeTab === 'upload'
                                  ? 'border-blue-600 text-blue-600'
                                  : 'border-transparent text-gray-500 hover:text-gray-700'
                              }`}
                            >
                              📁 Carica File
                            </button>
                            <button
                              onClick={() => setActiveTab('ai')}
                              className={`flex-1 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                                activeTab === 'ai'
                                  ? 'border-purple-600 text-purple-600'
                                  : 'border-transparent text-gray-500 hover:text-gray-700'
                              }`}
                            >
                              ✨ Genera AI
                            </button>
                            <button
                              onClick={() => setActiveTab('canva')}
                              className={`flex-1 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                                activeTab === 'canva'
                                  ? 'border-blue-600 text-blue-600'
                                  : 'border-transparent text-gray-500 hover:text-gray-700'
                              }`}
                            >
                              🎨 Canva
                            </button>
                          </div>

                          {/* Tab Content */}
                          <div className="bg-white rounded-lg p-4 border border-gray-200">
                            {activeTab === 'upload' && (
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Carica la Tua Etichetta
                                </label>
                                <CustomerLabelUpload 
                                  onImageUploaded={(imageUrl, file) => {
                                    setLabelImage(imageUrl);
                                    // Genera anteprima 3D automaticamente
                                    if (file) {
                                      generateBottlePreview(file);
                                    }
                                  }}
                                  currentImage={labelImage}
                                  skipUpload={true}
                                />
                              </div>
                            )}

                            {activeTab === 'ai' && (
                              <LabelGeneratorAI onLabelGenerated={handleAILabelGenerated} />
                            )}

                            {activeTab === 'canva' && (
                              <CanvaTemplateGallery />
                            )}
                          </div>
                        </div>
                      )}

                      {/* Info about custom graphics */}
                      {customGraphics && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <div className="flex">
                            <AlertCircle className="w-5 h-5 text-blue-500 mt-0.5 mr-2 flex-shrink-0" />
                            <div className="text-sm text-blue-700">
                              <p className="font-medium">Design Professionale Selezionato</p>
                              <p className="mt-1">Il nostro team di designer creerà una grafica personalizzata per il tuo liquore. Ti contatteremo per finalizzare i dettagli del design.</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <h4 className="text-md font-semibold text-gray-900 mb-4">Anteprima Bottiglia 3D</h4>
                    <div className="bg-gray-100 p-8 rounded-lg flex items-center justify-center min-h-[400px] relative">
                      {generatingPreview ? (
                        <div className="text-center">
                          <Loader className="w-12 h-12 text-purple-600 animate-spin mx-auto mb-4" />
                          <p className="text-sm text-gray-600">Generazione anteprima 3D...</p>
                          <p className="text-xs text-gray-500 mt-2">Tempo stimato: 15-30 secondi</p>
                        </div>
                      ) : bottlePreview && !customGraphics ? (
                        <div className="relative">
                          <img 
                            key={bottlePreview?.timestamp || bottlePreview}
                            src={getImageUrl(bottlePreview)} 
                            alt="Anteprima 3D Bottiglia" 
                            className="max-w-full max-h-[350px] object-contain rounded-lg shadow-xl cursor-pointer hover:scale-105 transition-transform"
                            onClick={() => setPreviewZoomed(true)}
                          />
                          <div className="absolute bottom-2 right-2 flex gap-2">
                            <button
                              onClick={downloadPreview}
                              className="bg-white/90 hover:bg-white p-2 rounded-full shadow-lg transition-all"
                              title="Scarica anteprima"
                            >
                              <Download className="w-5 h-5 text-gray-700" />
                            </button>
                            <button
                              onClick={() => setPreviewZoomed(true)}
                              className="bg-white/90 hover:bg-white p-2 rounded-full shadow-lg transition-all"
                              title="Ingrandisci"
                            >
                              <ZoomIn className="w-5 h-5 text-gray-700" />
                            </button>
                          </div>
                        </div>
                      ) : customGraphics ? (
                        <div className="bg-gradient-to-r from-purple-100 to-blue-100 p-6 rounded-lg shadow-lg border-2 border-purple-300 max-w-48">
                          <div className="text-center">
                            <div className="text-xs text-purple-600 mb-2 font-medium">DESIGN PROFESSIONALE</div>
                            <div className="text-lg font-bold mb-2 text-purple-800">
                              {labelText || 'Il Tuo Liquore'}
                            </div>
                            <div className="text-xs text-purple-500">Design personalizzato</div>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center text-gray-500">
                          <Package className="w-16 h-16 mx-auto mb-3 text-gray-400" />
                          <p className="text-sm">Carica la tua etichetta per vedere</p>
                          <p className="text-sm">l'anteprima 3D della bottiglia</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 6: Choose Bottle */}
            {currentStep === 6 && (
              <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 lg:p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Scegli la Bottiglia</h2>
                <div className="grid md:grid-cols-3 gap-6">
                  {bottleTypes && bottleTypes.map((bottle) => (
                    <div
                      key={bottle.id}
                      className={`border-2 rounded-lg p-6 transition-all duration-300 hover:shadow-lg ${
                        selectedBottle?.id === bottle.id ? 'border-purple-600 bg-purple-50' : 'border-gray-200 hover:border-purple-300'
                      }`}
                    >
                      <div className="relative">
                        <div className="aspect-square w-full overflow-hidden rounded-lg mb-4 cursor-pointer" onClick={() => setSelectedBottle(bottle)}>
                          <BuilderImage
                            src={getImageUrlWithFallback(bottle.image)} 
                            alt={bottle.name} 
                            className="rounded-lg hover:scale-105 transition-transform"
                            priority={false}
                          />
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setBottleZoomed(bottle);
                          }}
                          className="absolute top-2 right-2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg transition-all"
                          title="Ingrandisci bottiglia"
                        >
                          <ZoomIn className="w-4 h-4 text-gray-700" />
                        </button>
                      </div>
                      <div onClick={() => setSelectedBottle(bottle)} className="cursor-pointer">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">{bottle.name}</h3>
                        <p className="text-gray-600 text-sm mb-3">{bottle.description}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-8">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Quantità
                  </label>
                  <div className="flex items-center justify-center space-x-4 mb-4">
                    <button
                      onClick={() => setQuantity(Math.max(6, quantity - 6))}
                      className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                      title="Riduci di 6 bottiglie"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    
                    {/* Editable quantity input */}
                    <div className="relative">
                      <input
                        type="number"
                        min="6"
                        step="6"
                        value={quantity}
                        onChange={(e) => {
                          const value = parseInt(e.target.value) || 6;
                          setQuantity(Math.max(6, value));
                        }}
                        className="w-32 text-center text-lg font-semibold px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500 pointer-events-none">
                        btg
                      </span>
                    </div>
                    
                    <button
                      onClick={() => setQuantity(quantity + 6)}
                      className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                      title="Aggiungi 6 bottiglie"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Quick quantity selectors - multiples of 6 */}
                  <div className="space-y-2 mb-4">
                    <p className="text-xs text-center text-gray-500 font-medium">Selezione rapida:</p>
                    <div className="flex flex-wrap justify-center gap-2">
                      {[6, 12, 24, 48, 96, 144, 192, 288].map((qty) => (
                        <button
                          key={qty}
                          onClick={() => setQuantity(qty)}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                            quantity === qty
                              ? 'bg-purple-600 text-white shadow-md'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {qty} btg
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Ordine Minimo Warning - B2B */}
                  {quantity < 24 && (
                    <div className="p-3 bg-amber-50 border border-amber-300 rounded-lg mb-4">
                      <div className="flex items-start">
                        <AlertCircle className="w-5 h-5 text-amber-600 mr-2 flex-shrink-0 mt-0.5" />
                        <div className="text-sm">
                          <p className="font-semibold text-amber-900">Ordine minimo: 24 bottiglie</p>
                          <p className="text-amber-700 mt-1">
                            Mancano {24 - quantity} bottiglie per raggiungere il minimo. 
                            Puoi comunque procedere ma consigliamo multipli da 6 bottiglie.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Multiple of 6 suggestion */}
                  {quantity % 6 !== 0 && (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg mb-4">
                      <div className="flex items-start">
                        <Package className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-blue-800">
                          <p className="font-semibold">Consiglio: Ordina multipli di 6 bottiglie</p>
                          <p className="mt-1">Per ottimizzare l'imballaggio, considera di portare l'ordine a {Math.ceil(quantity / 6) * 6} bottiglie.</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Quantity Discount Info Removed */}
                </div>
              </div>
            )}
            </>
            
            {/* Navigation Buttons */}
            <div className="mt-8 max-w-md mx-auto">
              <div className="space-y-3">
                {currentStep < 6 ? (
                  <button
                    onClick={nextStep}
                    disabled={!canProceed()}
                    className={`w-full flex items-center justify-center px-4 py-3 rounded-lg font-semibold transition-colors duration-200 ${
                      canProceed()
                        ? 'bg-purple-600 text-white hover:bg-purple-700'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    Continua
                    <ChevronRight className="ml-2 w-4 h-4" />
                  </button>
                ) : (
                  <button
                    onClick={addToCart}
                    disabled={!canProceed() || addingToCart}
                    className={`w-full flex items-center justify-center px-4 py-3 rounded-lg font-semibold transition-colors duration-200 ${
                      canProceed() && !addingToCart
                        ? 'bg-green-600 text-white hover:bg-green-700'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    {addingToCart ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Aggiungendo...
                      </>
                    ) : (
                      <>
                        <ShoppingCart className="mr-2 w-4 h-4" />
                        Aggiungi al Preventivo
                      </>
                    )}
                  </button>
                )}
                
                {currentStep > 1 && (
                  <button
                    onClick={prevStep}
                    className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                  >
                    <ChevronLeft className="mr-2 w-4 h-4" />
                    Indietro
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Zoom Modal for 3D Preview */}
      {previewZoomed && bottlePreview && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setPreviewZoomed(false)}
        >
          <div className="relative max-w-4xl max-h-[90vh]">
            <img 
              src={getImageUrl(bottlePreview)} 
              alt="Anteprima 3D Bottiglia - Ingrandita" 
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              onClick={() => setPreviewZoomed(false)}
              className="absolute top-4 right-4 bg-white/90 hover:bg-white p-3 rounded-full shadow-lg transition-all"
            >
              <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Zoom Modal for Bottle Selection */}
      {bottleZoomed && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setBottleZoomed(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh] flex flex-col items-center">
            <img 
              src={getImageUrlWithFallback(bottleZoomed.image)} 
              alt={`${bottleZoomed.name} - Ingrandita`} 
              className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
            <div className="mt-4 bg-white/95 rounded-lg px-6 py-4 shadow-xl max-w-md" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-xl font-bold text-gray-900 mb-2">{bottleZoomed.name}</h3>
              <p className="text-gray-600 text-sm mb-3">{bottleZoomed.description}</p>
              {bottleZoomed.price > 0 ? (
                <div className="flex flex-col items-start">
                  <p className="text-lg font-bold text-purple-600">
                    +€{(bottleZoomed.price / 1.22).toFixed(2)}
                  </p>
                  <p className="text-xs text-gray-500">IVA esclusa</p>
                </div>
              ) : (
                <p className="text-lg font-bold text-purple-600">Inclusa</p>
              )}
            </div>
            <button
              onClick={() => setBottleZoomed(null)}
              className="absolute top-4 right-4 bg-white/90 hover:bg-white p-3 rounded-full shadow-lg transition-all"
            >
              <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
      
      {/* Mobile Sticky Navigation Bar - REMOVED */}
      {/* Sticky navigation removed for better mobile UX with auto-advance */}
      
      <Footer />
    </div>
  );
};

export default Builder;